// Utilitaires pour la gestion multi-pays Amazon

// Liste des pays Amazon Europe
export const COUNTRIES = [
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'DE', name: 'Allemagne', flag: '🇩🇪' },
  { code: 'ES', name: 'Espagne', flag: '🇪🇸' },
  { code: 'IT', name: 'Italie', flag: '🇮🇹' },
  { code: 'UK', name: 'Royaume-Uni', flag: '🇬🇧' },
  { code: 'BE', name: 'Belgique', flag: '🇧🇪' },
  { code: 'NL', name: 'Pays-Bas', flag: '🇳🇱' },
  { code: 'PL', name: 'Pologne', flag: '🇵🇱' },
  { code: 'SE', name: 'Suède', flag: '🇸🇪' },
  { code: 'IE', name: 'Irlande', flag: '🇮🇪' }
];

// Pays par défaut
export const DEFAULT_COUNTRY = 'FR';

// Option pour "Tous les pays" dans le filtre
export const ALL_COUNTRIES_FILTER = '__ALL__';

// Créer une clé composite pays_sku
export const getCompositeKey = (country, sku) => {
  return `${country}_${sku}`;
};

// Parser une clé composite pour récupérer pays et sku
export const parseCompositeKey = (compositeKey) => {
  if (!compositeKey) return { country: DEFAULT_COUNTRY, sku: '' };

  const parts = compositeKey.split('_');
  if (parts.length < 2) {
    // Ancien format sans pays - considérer comme FR par défaut
    return { country: DEFAULT_COUNTRY, sku: compositeKey };
  }

  const country = parts[0];
  const sku = parts.slice(1).join('_'); // Gérer le cas où le SKU contient des underscores

  return { country, sku };
};

// Vérifier si une clé est au nouveau format (avec pays)
export const isCompositeKey = (key) => {
  if (!key) return false;
  const parts = key.split('_');
  if (parts.length < 2) return false;

  const possibleCountry = parts[0];
  return COUNTRIES.some(country => country.code === possibleCountry);
};

// Migrer une clé ancienne vers le nouveau format
export const migrateKeyToComposite = (key, defaultCountry = DEFAULT_COUNTRY) => {
  if (isCompositeKey(key)) return key;
  return getCompositeKey(defaultCountry, key);
};

// Obtenir le pays à partir d'une clé
export const getCountryFromKey = (key) => {
  const { country } = parseCompositeKey(key);
  return country;
};

// Obtenir le SKU à partir d'une clé
export const getSkuFromKey = (key) => {
  const { sku } = parseCompositeKey(key);
  return sku;
};

// Filtrer les données par pays
export const filterDataByCountry = (data, countryFilter) => {
  if (countryFilter === ALL_COUNTRIES_FILTER) return data;

  const filtered = {};
  Object.entries(data).forEach(([key, value]) => {
    const country = getCountryFromKey(key);
    if (country === countryFilter) {
      filtered[key] = value;
    }
  });

  return filtered;
};

// Obtenir tous les pays présents dans les données
export const getAvailableCountries = (data) => {
  const countriesInData = new Set();

  Object.keys(data).forEach(key => {
    const country = getCountryFromKey(key);
    countriesInData.add(country);
  });

  return COUNTRIES.filter(country => countriesInData.has(country.code));
};

// Migrer un objet de données du format ancien vers le nouveau
export const migrateDataToComposite = (data, defaultCountry = DEFAULT_COUNTRY) => {
  const migrated = {};
  let migrationNeeded = false;

  Object.entries(data).forEach(([key, value]) => {
    if (isCompositeKey(key)) {
      migrated[key] = value;
    } else {
      const newKey = migrateKeyToComposite(key, defaultCountry);
      migrated[newKey] = value;
      migrationNeeded = true;
    }
  });

  if (migrationNeeded) {
    console.log(`Migration needed: converted ${Object.keys(data).length} keys to composite format with country ${defaultCountry}`);
  }

  return { migratedData: migrated, migrationNeeded };
};

// Grouper les données par pays
export const groupDataByCountry = (data) => {
  const grouped = {};

  Object.entries(data).forEach(([key, value]) => {
    const country = getCountryFromKey(key);
    if (!grouped[country]) {
      grouped[country] = {};
    }
    grouped[country][key] = value;
  });

  return grouped;
};

// Obtenir les informations d'un pays par son code
export const getCountryInfo = (countryCode) => {
  return COUNTRIES.find(country => country.code === countryCode) ||
         { code: countryCode, name: countryCode, flag: '🏳️' };
};

// Calculer les totaux par pays
export const calculateCountryTotals = (groupedTransactions, costs, countryFilter) => {
  if (countryFilter !== ALL_COUNTRIES_FILTER) {
    // Pour un pays spécifique, retourner les totaux existants
    return null;
  }

  const countryTotals = {};

  groupedTransactions.forEach(group => {
    const country = group.country;
    if (!countryTotals[country]) {
      countryTotals[country] = {
        totalCa: 0,
        totalProfit: 0,
        totalCaHT: 0
      };
    }

    countryTotals[country].totalCa += group.totalCa || 0;
    // Le calcul du profit sera fait dans le composant principal
  });

  return countryTotals;
};

// Couleurs pour les marges selon les seuils
export const getMarginColor = (marginPercent) => {
  if (marginPercent < 8) return '#DC2626'; // Rouge
  if (marginPercent < 15) return '#F97316'; // Orange
  return '#059669'; // Vert
};

export default {
  COUNTRIES,
  DEFAULT_COUNTRY,
  ALL_COUNTRIES_FILTER,
  getCompositeKey,
  parseCompositeKey,
  isCompositeKey,
  migrateKeyToComposite,
  getCountryFromKey,
  getSkuFromKey,
  filterDataByCountry,
  getAvailableCountries,
  migrateDataToComposite,
  groupDataByCountry,
  getCountryInfo,
  calculateCountryTotals,
  getMarginColor
};